/**
 * URL scrubbing utility for Datadog telemetry (RUM + browser logs).
 *
 * Pentest findings #2 / #4: Datadog RUM and browser-logs captured OAuth `code`,
 * `state`, `token`, `redirect=<encoded URL with code>`, and OIDC fragment-borne
 * `access_token`/`id_token`/`session_state` values verbatim in session payloads.
 *
 * Coverage:
 *  - Query-string keys (case-insensitive) listed in {@link REDACTED_QS_KEYS}.
 *  - URL fragments (`#key=value&...`), because OAuth implicit flow and OIDC put
 *    `access_token`, `id_token`, `state`, and `session_state` in the fragment.
 *    Fragments never reach the server but client-side telemetry captures them.
 *  - Recursive scrub of any `redirect=<url>` value, because the observed attack
 *    pattern was `/oauth/post-auth?redirect=<encoded url with code>`.
 *  - Relative URLs (`/verify?token=...`, `?token=...`, `#access_token=...`),
 *    because RUM event fields (resource URLs, action target names) are not
 *    guaranteed to be absolute.
 */

/** Query-string keys whose values must be redacted before telemetry is sent. */
export const REDACTED_QS_KEYS: ReadonlySet<string> = new Set([
  'code',
  'token',
  'access_token',
  'refresh_token',
  'id_token',
  'state',
  'session_state',
  'authorization',
  'client_secret',
  'password'
]);

const REDACTED_PLACEHOLDER = 'REDACTED';
const PLACEHOLDER_BASE = 'http://placeholder.local/';
const ABSOLUTE_URL_PATTERN = /^[a-z][a-z0-9+\-.]*:/i;

function isAbsolute(raw: string): boolean {
  // Treat scheme-relative URLs (//example.com/...) as absolute too.
  return raw.startsWith('//') || ABSOLUTE_URL_PATTERN.test(raw);
}

/**
 * Scrub sensitive keys in a URLSearchParams bag in place.
 * Returns true if any key was modified.
 */
function scrubParams(params: URLSearchParams): boolean {
  let touched = false;
  const keys = Array.from(new Set(Array.from(params.keys())));
  for (const key of keys) {
    const lower = key.toLowerCase();
    if (REDACTED_QS_KEYS.has(lower)) {
      const values = params.getAll(key);
      params.delete(key);
      for (let i = 0; i < values.length; i += 1) {
        params.append(key, REDACTED_PLACEHOLDER);
      }
      touched = true;
    } else if (lower === 'redirect') {
      // The redirect value is itself a URL that may contain `code=...` etc.
      const values = params.getAll(key);
      params.delete(key);
      for (const value of values) {
        const scrubbed = scrubUrl(value) ?? value;
        params.append(key, scrubbed);
      }
      touched = true;
    }
  }
  return touched;
}

/**
 * Scrub a URL fragment.
 *
 * Only treats fragments as param-shaped when they contain at least one `=` —
 * OAuth implicit flow / OIDC use `#key=value&key=value`, while SPA route
 * fragments (`#/users/123`) and anchor fragments (`#section-2`) do not.
 * Non-param fragments are preserved unchanged.
 */
function scrubFragment(hash: string): string {
  if (!hash || hash === '#' || !hash.includes('=')) return hash;
  const body = hash.startsWith('#') ? hash.slice(1) : hash;
  const params = new URLSearchParams(body);
  const touched = scrubParams(params);
  return touched ? `#${params.toString()}` : hash;
}

/**
 * Scrub sensitive params from a URL string (absolute or relative).
 *
 * - Empty / undefined → returned unchanged.
 * - Strings with no `?` and no `#` → returned unchanged (nothing to scrub).
 * - Parse failure → returned unchanged (never throws).
 * - Otherwise: search params and fragment are scrubbed; the original shape
 *   (absolute / leading-slash relative / query-only / fragment-only) is
 *   preserved on output.
 */
export function scrubUrl(raw: string | undefined): string | undefined {
  if (raw === undefined || raw === '') return raw;
  // Fast path: no query, no fragment → nothing we can scrub. Returning the
  // input verbatim also keeps non-URL strings (free-form action target names)
  // untouched and avoids URL constructor side effects (e.g. space encoding).
  if (!raw.includes('?') && !raw.includes('#')) return raw;

  const absolute = isAbsolute(raw);
  let parsed: URL;
  try {
    parsed = absolute ? new URL(raw) : new URL(raw, PLACEHOLDER_BASE);
  } catch {
    return raw;
  }

  scrubParams(parsed.searchParams);
  parsed.hash = scrubFragment(parsed.hash);

  if (absolute) return parsed.toString();

  // Re-derive the original relative shape so we don't mutate inputs like
  // `?token=ABC` into `http://placeholder.local/?token=REDACTED`.
  if (raw.startsWith('?')) return parsed.search + parsed.hash;
  if (raw.startsWith('#')) return parsed.hash;
  if (raw.startsWith('/')) return parsed.pathname + parsed.search + parsed.hash;
  // Bare relative path (e.g. `oauth-callback?code=ABC`).
  return parsed.pathname.replace(/^\//, '') + parsed.search + parsed.hash;
}
